import React, { useContext, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MedicationIcon from "@mui/icons-material/Medication";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { UserContext } from "../../hooks/userContext";
import { useQueryWantedItems } from "../../queries/wantedItem/hooks/useQueryWantedItems";
import { useMutationDeleteWantedItem } from "../../queries/wantedItem/hooks/useMutationDeleteWantedItem";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { routeConfigs } from "../../navigation/routeConfig";
import { ToasterContext } from "../../components/shared/Toaster/ToasterContext";
import { NotificationType } from "../../constants/global";
import axios from "axios";
import { pageTitleSx } from "../../constants/designTokens";

const INK = "#1E293B";
const INK_SOFT = "#475569";
const LINE_SOFT = "#F1F5F9";
const PRIMARY = "#2563EB";
const PRIMARY_DARK = "#1D4ED8";
const BODY_FONT = '"Source Sans 3", sans-serif';
const DISPLAY_FONT = '"Lexend", sans-serif';
const MONO_FONT = '"Spline Sans Mono", monospace';

const DrugThumb = ({ ndc }: { ndc?: string }) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!ndc) return;
    const formattedNdc = ndc.replace(/-/g, "");
    setIsLoading(true);
    axios
      .get(`${process.env.REACT_APP_API_URL}/drug-images?ndc=${formattedNdc}`)
      .then((response) => {
        setImageUrl(response?.data?.imageUrl || null);
      })
      .catch(() => {
        setImageUrl(null);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [ndc]);

  const thumbSx = {
    width: 48,
    height: 48,
    borderRadius: "8px",
    flexShrink: 0,
    overflow: "hidden",
  };

  if (isLoading) {
    return <Box sx={{ ...thumbSx, bgcolor: LINE_SOFT }} />;
  }

  if (imageUrl) {
    return (
      <Box
        component="img"
        src={imageUrl}
        alt="Drug"
        sx={{
          ...thumbSx,
          border: `1px solid ${LINE_SOFT}`,
          objectFit: "cover",
          display: "block",
          bgcolor: "#fff",
        }}
      />
    );
  }

  return (
    <Box
      sx={{
        ...thumbSx,
        bgcolor: "#F1F5F9",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <MedicationIcon sx={{ fontSize: 24, color: PRIMARY }} />
    </Box>
  );
};

const Wishlist: React.FunctionComponent = () => {
  const { id }: any = useContext(UserContext);
  const { addToast } = useContext(ToasterContext);
  const navigate = useNavigate();
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

  const { data, loading: queryLoading, refetch } = useQueryWantedItems({
    variables: {
      where: {
        buyerUserId: +id,
      },
    },
    fetchPolicy: "no-cache",
    pollInterval: 5000,
  });

  const [deleteWantedItem, { loading: deleteLoading }] = useMutationDeleteWantedItem({
    onCompleted: () => {
      addToast({ type: NotificationType.Success, title: "Wishlist item deleted." });
      refetch();
    },
    onError: (error: any) => {
      addToast({ type: NotificationType.Error, title: error.message });
    },
  });

  const handleDelete = (wantedItemId: number) => {
    deleteWantedItem({
      variables: {
        where: {
          id: wantedItemId,
        },
      },
    });
  };

  const handleConfirmDelete = () => {
    if (!deleteConfirmId) return;
    handleDelete(deleteConfirmId);
    setDeleteConfirmId(null);
  };

  const items = useMemo(() => data?.findManyWantedItem || [], [data?.findManyWantedItem]);
  const totalCount = items.length;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        bgcolor: "#F8FAFC",
        fontFamily: BODY_FONT,
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: "flex",
          flexWrap: "wrap",
          gap: 1.5,
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: `1px solid ${LINE_SOFT}`,
          bgcolor: "#fff",
        }}
      >
        <Box>
          <Typography component="h1" sx={pageTitleSx}>
            Wishlist
          </Typography>
          <Box sx={{ fontSize: 12.5, color: INK_SOFT }}>
            {totalCount} {totalCount === 1 ? "item" : "items"} saved
          </Box>
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddRoundedIcon />}
          onClick={() => navigate(routeConfigs.newWishlistItem.path)}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "8px",
            px: 1.75,
            boxShadow: "none",
            backgroundColor: PRIMARY,
            "&:hover": { backgroundColor: PRIMARY_DARK, boxShadow: "none" },
          }}
        >
          Add to wishlist
        </Button>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", p: 2 }}>
        {queryLoading && items.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} sx={{ color: PRIMARY }} />
          </Box>
        ) : items.length === 0 ? (
          <Box
            sx={{
              maxWidth: 380,
              p: 3,
              textAlign: "center",
              bgcolor: "#fff",
              borderRadius: "12px",
              border: `1px solid ${LINE_SOFT}`,
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "10px",
                bgcolor: "#F1F5F9",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 1.5,
              }}
            >
              <MedicationIcon sx={{ fontSize: 24, color: PRIMARY }} />
            </Box>
            <Typography sx={{ fontFamily: DISPLAY_FONT, fontWeight: 700, fontSize: 16, color: INK, mb: 0.5 }}>
              No wishlist items yet
            </Typography>
            <Typography sx={{ fontSize: 13, color: INK_SOFT, mb: 2 }}>
              Save drugs you want to buy so you can find them quickly later.
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddRoundedIcon />}
              onClick={() => navigate(routeConfigs.newWishlistItem.path)}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "8px",
                boxShadow: "none",
                backgroundColor: PRIMARY,
                "&:hover": { backgroundColor: PRIMARY_DARK, boxShadow: "none" },
              }}
            >
              Add to wishlist
            </Button>
          </Box>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                md: "repeat(3, minmax(0, 1fr))",
                xl: "repeat(4, minmax(0, 1fr))",
              },
              gap: 1.5,
            }}
          >
            {items.map((item: any) => {
              const metaLine = [item.manufacturer, item.dosageForm].filter(Boolean).join(" · ");
              const detailLine = [item.ndcNumber, item.strength].filter(Boolean).join(" · ");

              return (
                <Box
                  key={item.id}
                  sx={{
                    display: "flex",
                    gap: 1.25,
                    alignItems: "center",
                    p: 1.5,
                    bgcolor: "#fff",
                    borderRadius: "10px",
                    border: `1px solid ${LINE_SOFT}`,
                    minHeight: 76,
                    transition: "border-color .15s ease, box-shadow .15s ease",
                    "&:hover": {
                      borderColor: "#E2E8F0",
                      boxShadow: "0 4px 14px rgba(15, 23, 42, 0.06)",
                    },
                  }}
                >
                  <DrugThumb ndc={item.ndcNumber} />

                  <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", justifyContent: "center", gap: 0.25 }}>
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75 }}>
                      <Typography
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          fontWeight: 600,
                          fontSize: "14.5px",
                          color: INK,
                          lineHeight: 1.25,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {item.drugName || "Untitled drug"}
                      </Typography>
                      <IconButton
                        aria-label="Delete wishlist item"
                        onClick={() => setDeleteConfirmId(+item.id)}
                        disabled={deleteLoading}
                        size="small"
                        sx={{
                          color: INK_SOFT,
                          flexShrink: 0,
                          mt: -0.5,
                          mr: -0.5,
                          borderRadius: "8px",
                          "&:hover": { color: "#A02121", bgcolor: "#F9E3E3" },
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    {detailLine && (
                      <Box
                        sx={{
                          fontFamily: MONO_FONT,
                          fontSize: "11px",
                          color: INK_SOFT,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {detailLine}
                      </Box>
                    )}

                    {metaLine && (
                      <Box
                        sx={{
                          fontSize: "12.5px",
                          color: INK_SOFT,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {metaLine}
                      </Box>
                    )}

                    <Box sx={{ fontFamily: MONO_FONT, fontSize: "10.5px", color: "#475569" }}>
                      Added {moment(item.createdAt).format("MM/DD/YYYY")}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      <Dialog open={deleteConfirmId !== null} onClose={() => setDeleteConfirmId(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontFamily: DISPLAY_FONT, fontWeight: 700 }}>Delete wishlist item</DialogTitle>
        <DialogContent sx={{ color: INK_SOFT }}>
          Are you sure you want to delete this item?
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" onClick={() => setDeleteConfirmId(null)} sx={{ textTransform: "none", borderRadius: "8px" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={deleteLoading}
            sx={{ textTransform: "none", borderRadius: "8px", boxShadow: "none" }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Wishlist;
